/**
 * Módulo Adquisición — `/adquisicion`. Admin y Comercial.
 *
 * La página orquesta y no dibuja: trae los datos, deriva lo que hace falta con
 * `derivar.ts` (puro y testeado) y reparte en tres bloques. Cada bloque es un
 * archivo aparte con una sola responsabilidad.
 *
 * Tres cosas que atraviesan la pantalla:
 *
 *  - **El período sale del shell** (`usePeriodo`), nunca de estado propio. Si cada
 *    tabla tuviera el suyo, el funnel podría mostrar junio al lado de un canal de
 *    julio y nadie lo notaría.
 *  - **La comparación contra el mes anterior es una segunda consulta**
 *    (`useFunnel(periodoAnterior)`). Si falla, la pantalla no se cae: los deltas
 *    quedan sin publicar y el pie del panel dice el mensaje que mandó la API.
 *  - **La plata la recorta el servidor.** El front además no dibuja la columna:
 *    para comercial `costoMensual` y `valorEstimado` llegan en `null`, y un "—"
 *    ahí se leería como "sin dato" cuando en realidad es "sin permiso".
 */
import { useMemo, useState } from 'react'

import { mensajeDeError } from '../../lib/api'
import { irA } from '../../lib/scroll'
import {
  formatMetricValue,
  formatPeriodo,
  periodoAnterior,
  plural,
} from '../../lib/format'
import { useAcquisition, useFunnel, useMe } from '../../lib/queries'
import { usePeriodo } from '../../shell/AppShell'
import { Button, Section, Stack, TriageBar, type TriageConteo } from '../../ui'
import { CanalesBlock } from './CanalesBlock'
import {
  conteosAdquisicion,
  filasEtapa,
  SEO_PRIMERA_PAGINA,
  SEO_SEGUNDA_PAGINA,
  severidadSeo,
  titularAdquisicion,
} from './derivar'
import { Cargando, ErrorDeCarga } from '../comunes'
import { FunnelBlock } from './FunnelBlock'
import { OportunidadesBlock } from './OportunidadesBlock'
import { SeoBlock } from './SeoBlock'

const ID_FUNNEL = 'adquisicion-funnel'
const ID_CANALES = 'adquisicion-canales'
const ID_SEO = 'adquisicion-seo'

export default function AdquisicionPage() {
  const { periodo } = usePeriodo()
  // Un solo "ahora" para toda la pantalla: si cada badge de frescura instanciara
  // su propio Date, dos cargas del mismo minuto podrían decir días distintos.
  const [now] = useState(() => new Date())

  const me = useMe()
  const actual = useAcquisition(periodo)

  const periodoBase = periodoAnterior(periodo)
  // `periodoBase` solo es null con un período mal formado, que el selector del
  // shell no puede producir. Se pide el actual para no mandar un período vacío a
  // la API, y el dato se descarta más abajo.
  const anterior = useFunnel(periodoBase ?? periodo)

  // Sin usuario resuelto el default es el rol más restrictivo: mostrar plata de
  // más es un incidente, mostrarla de menos es una columna que aparece un
  // instante después.
  const rol = me.data?.rol ?? 'comercial'

  const datapointsAnteriores = useMemo(
    () => (periodoBase === null ? [] : (anterior.data?.datapoints ?? [])),
    [periodoBase, anterior.data],
  )

  const filas = useMemo(
    () =>
      filasEtapa({
        etapas: actual.data?.etapas ?? [],
        datapoints: actual.data?.datapoints ?? [],
        datapointsAnteriores,
        periodoBase,
      }),
    [actual.data, datapointsAnteriores, periodoBase],
  )

  if (actual.isLoading) {
    return <Cargando que={`la adquisición de ${formatPeriodo(periodo)}`} />
  }

  if (actual.error !== null || actual.data === undefined) {
    return (
      <ErrorDeCarga
        que={`la adquisición de ${formatPeriodo(periodo)}`}
        error={actual.error}
        onRetry={() => void actual.refetch()}
        reintentando={actual.isFetching}
      />
    )
  }

  const { etapas, datapoints, canales, seoQueries, oportunidades, links } =
    actual.data

  const conteos = conteosAdquisicion({
    filas,
    canales,
    seoQueries,
    oportunidades,
  })
  const { estado, titular } = titularAdquisicion(filas, conteos)

  // Cada conteo escribe su etiqueta con `plural`: la barra imprime el número
  // pegado al label, así que un singular cableado produce "1 canales".
  const triage: TriageConteo[] = [
    {
      severidad: 'critical' as const,
      cantidad: conteos.etapasSinCargar,
      label: `${plural(conteos.etapasSinCargar, 'etapa', 'etapas')} sin cargar`,
      onClick: () => irA(ID_FUNNEL),
    },
    {
      severidad: 'warning' as const,
      cantidad: conteos.cargasVencidas,
      label: `${plural(conteos.cargasVencidas, 'carga vencida', 'cargas vencidas')}`,
      onClick: () => irA(ID_CANALES),
    },
    {
      severidad: 'warning' as const,
      cantidad: conteos.canalesSinEvaluar,
      label: `${plural(conteos.canalesSinEvaluar, 'canal', 'canales')} sin evaluar`,
      onClick: () => irA(ID_CANALES),
    },
    {
      severidad: 'warning' as const,
      cantidad: conteos.queriesSinMedicion,
      label: `${plural(conteos.queriesSinMedicion, 'query', 'queries')} sin medición`,
      onClick: () => irA(ID_SEO),
    },
    {
      severidad: 'warning' as const,
      cantidad: conteos.oportunidadesFueraDelCrm,
      label: `${plural(conteos.oportunidadesFueraDelCrm, 'oportunidad', 'oportunidades')} fuera de Close`,
      onClick: () => irA(ID_FUNNEL),
    },
  ].filter((conteo) => conteo.cantidad > 0)

  const conteosVisibles: TriageConteo[] =
    triage.length > 0
      ? triage
      : [
          {
            severidad: filas.length === 0 ? 'unknown' : 'ok',
            cantidad: filas.length,
            label: `${plural(filas.length, 'etapa', 'etapas')} del funnel con dato`,
          },
        ]

  const entrada = filas[0] ?? null
  const salida = filas.length > 0 ? (filas[filas.length - 1] ?? null) : null
  const cuello = filas.find((fila) => fila.esCuelloDeBotella) ?? null

  const enPrimeraPagina = seoQueries.filter(
    (query) => severidadSeo(query) === 'ok',
  ).length
  const canalesEvaluados = canales.length - conteos.canalesSinEvaluar

  return (
    <>
      <TriageBar
        estado={estado}
        titular={titular}
        conteos={conteosVisibles}
        meta={`datos de ${formatPeriodo(periodo)}`}
        acciones={
          <>
            {conteos.canalesSinEvaluar > 0 ? (
              <Button
                variante="secundario"
                icono="megafono"
                onClick={() => irA(ID_CANALES)}
              >
                {conteos.canalesSinEvaluar === 1
                  ? 'Evaluar el canal sin medir'
                  : `Evaluar los ${formatMetricValue(conteos.canalesSinEvaluar, 'count')} canales sin medir`}
              </Button>
            ) : null}
            {conteos.etapasSinCargar > 0 ? (
              <Button
                variante="primario"
                icono="lapiz"
                onClick={() => irA(ID_FUNNEL)}
              >
                {conteos.etapasSinCargar === 1
                  ? 'Cargar la etapa que falta'
                  : `Cargar las ${formatMetricValue(conteos.etapasSinCargar, 'count')} etapas que faltan`}
              </Button>
            ) : null}
          </>
        }
        className="mb-5 rounded-card border"
      />

      <Section
        id={ID_FUNNEL}
        kicker="Zona 1"
        titulo="Cómo viene la máquina este mes"
        bajada={
          <BajadaFunnel
            periodo={periodo}
            entrada={entrada}
            salida={salida}
            cuello={cuello}
          />
        }
      >
        <Stack gap="md">
          <FunnelBlock
            filas={filas}
            periodo={periodo}
            periodoBase={periodoBase}
            errorComparacion={
              anterior.error !== null ? mensajeDeError(anterior.error) : null
            }
            now={now}
          />
          <OportunidadesBlock
            oportunidades={oportunidades}
            etapas={etapas}
            canales={canales}
            links={links.oportunidades}
            rol={rol}
          />
        </Stack>
      </Section>

      <Section
        id={ID_CANALES}
        kicker="Zona 2"
        titulo="Qué canal se merece la próxima hora"
        bajada={
          canales.length === 0 ? (
            <>Todavía no hay ningún canal cargado, así que no hay nada que comparar.</>
          ) : conteos.canalesSinEvaluar === 0 ? (
            <>
              {plural(canales.length, 'El', 'Los')}{' '}
              <b>{formatMetricValue(canales.length, 'count')}</b>{' '}
              {plural(canales.length, 'canal tiene', 'canales tienen')} su
              conversión medida en el CRM. Acá se comparan por los leads del mes,
              que salen del desglose del funnel de arriba: el porcentaje del CRM
              no se puede recalcular con nada de esta pantalla, así que no se
              imprime.
            </>
          ) : (
            <>
              <b>{formatMetricValue(canalesEvaluados, 'count')}</b> de{' '}
              {formatMetricValue(canales.length, 'count')}{' '}
              {plural(canales.length, 'canal tiene', 'canales tienen')} conversión
              medida en el CRM.{' '}
              {conteos.canalesSinEvaluar === 1 ? (
                <>
                  El otro va en gris y dice <b>Sin evaluar</b>: no sabemos si
                  sirve, y por eso no aparece en verde.
                </>
              ) : (
                <>
                  Los otros{' '}
                  <b>{formatMetricValue(conteos.canalesSinEvaluar, 'count')}</b>{' '}
                  van en gris y dicen <b>Sin evaluar</b>: no sabemos si sirven, y
                  por eso no aparecen en verde.
                </>
              )}{' '}
              El semáforo de la columna Escalabilidad habla de escalabilidad, no
              de resultados.
            </>
          )
        }
      >
        <CanalesBlock
          canales={canales}
          etapas={etapas}
          datapoints={datapoints}
          periodo={periodo}
          rol={rol}
          now={now}
        />
      </Section>

      <Section
        id={ID_SEO}
        kicker="Zona 3"
        titulo="Dónde estamos peleando en Google"
        espaciado="sm"
        bajada={
          seoQueries.length === 0 ? (
            <>No hay queries objetivo cargadas para este período.</>
          ) : (
            <>
              <b>{formatMetricValue(enPrimeraPagina, 'count')}</b> de{' '}
              {formatMetricValue(seoQueries.length, 'count')}{' '}
              {plural(seoQueries.length, 'query está', 'queries están')} en la
              primera página. El semáforo es la página del resultado: hasta la
              posición <b>{formatMetricValue(SEO_PRIMERA_PAGINA, 'count')}</b> es
              primera página, hasta{' '}
              <b>{formatMetricValue(SEO_SEGUNDA_PAGINA, 'count')}</b> segunda, y
              más abajo nadie la ve.
            </>
          )
        }
      >
        <SeoBlock queries={seoQueries} links={links.seoQueries} now={now} />
      </Section>
    </>
  )
}

interface BajadaFunnelProps {
  periodo: string
  entrada: { nombre: string; cantidad: number | null } | null
  salida: { nombre: string; cantidad: number | null } | null
  cuello: {
    nombre: string
    conversionPct: number | null
    baseConversion: { nombre: string; cantidad: number } | null
  } | null
}

/**
 * La conclusión antes de la tabla, con las cifras en `<b>`. Cuando falta un dato
 * lo dice en lugar de completar con un cero: es la misma regla que la tarjeta de
 * KPI, aplicada a la prosa.
 */
function BajadaFunnel({ periodo, entrada, salida, cuello }: BajadaFunnelProps) {
  if (entrada === null || salida === null) {
    return <>Todavía no hay etapas de funnel configuradas para {formatPeriodo(periodo)}.</>
  }

  const volumen =
    entrada.cantidad === null || salida.cantidad === null ? (
      <>
        El funnel de {formatPeriodo(periodo)} está incompleto: falta el agregado
        de al menos una etapa, así que no se puede leer de punta a punta.
      </>
    ) : (
      <>
        De <b>{formatMetricValue(entrada.cantidad, 'count')}</b>{' '}
        {entrada.nombre.toLowerCase()} en {formatPeriodo(periodo)} salieron{' '}
        <b>{formatMetricValue(salida.cantidad, 'count')}</b>{' '}
        {salida.nombre.toLowerCase()}.
      </>
    )

  if (cuello === null || cuello.conversionPct === null) {
    return volumen
  }

  return (
    <>
      {volumen} El paso más angosto es <b>{cuello.nombre}</b>, con{' '}
      <b>{formatMetricValue(cuello.conversionPct, 'percent')}</b>
      {cuello.baseConversion === null ? null : (
        <>
          {' '}
          desde {cuello.baseConversion.nombre} (
          {formatMetricValue(cuello.baseConversion.cantidad, 'count')})
        </>
      )}
      .
    </>
  )
}
